import { useQuery, useQueryClient } from 'react-query';
import type { GetPlaylistParams, Playlist } from '@jwp/ott-common/types/playlist';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { generatePlaylistPlaceholder } from '@jwp/ott-common/src/utils/collection';
import { isScheduledOrLiveMedia } from '@jwp/ott-common/src/utils/liveEvent';
import { isTruthyCustomParamValue } from '@jwp/ott-common/src/utils/common';
import type { ApiError } from '@jwp/ott-common/src/utils/api';
import { useContentStateStore } from '@jwp/ott-common/src/stores/ContentStateStore';
import { PLAYLIST_INIT_OFFSET, PLAYLIST_LIMIT } from '@jwp/ott-common/src/constants';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';

const placeholderData = generatePlaylistPlaceholder(30);

export default function usePlaylist(playlistId?: string, params: GetPlaylistParams = {}, enabled = true, usePlaceholderData = false) {
  const apiService = getModule(ApiService);
  const queryClient = useQueryClient();
  const contentStore = useContentStateStore((s) => s.pages);
  const setPageState = useContentStateStore((s) => s.setPageState);
  const pageStatePlaylist = playlistId ? contentStore[playlistId] : undefined;
  const callback = async (playlistId?: string, params?: GetPlaylistParams) => {
    const hasPrevLoadedItems = pageStatePlaylist?.totalLoaded !== undefined && pageStatePlaylist.totalLoaded > 0;
    const pageLimit = hasPrevLoadedItems ? (pageStatePlaylist?.pageOffset + PLAYLIST_LIMIT).toString() : PLAYLIST_LIMIT.toString();
    const pageOffset = (hasPrevLoadedItems ? 1 : PLAYLIST_INIT_OFFSET).toString();

    const config = useConfigStore.getState().config;
    const isReels = config.content?.find((c) => c.contentId === playlistId)?.custom?.mediaType === 'reels';
    const isVideo = config.content?.find((c) => c.contentId === playlistId)?.custom?.mediaType === 'videos';

    let playlist;
    if (isReels && playlistId) {
      playlist = await apiService.getReelsByPlaylistId(
        playlistId,
        parseInt(pageLimit),
        (parseInt(pageOffset) - 1) * PLAYLIST_LIMIT,
        params?.mediaId,
      );
    } else if (isVideo && playlistId) {
      playlist = await apiService.getVideosByPlaylistId(
        playlistId,
        parseInt(pageLimit),
        (parseInt(pageOffset) - 1) * PLAYLIST_LIMIT,
      );
    } else {
      playlist = await apiService.getPlaylistById(playlistId, {
        page_limit: pageLimit,
        page_offset: pageOffset,
        ...params,
      });
    }

    if (playlist?.links?.next && playlistId) {
      setPageState(playlistId, {
        hasNext: Boolean(playlist?.links?.next),
      });
    }

    // This pre-caches all playlist items and makes navigating a lot faster.
    playlist?.playlist?.forEach((playlistItem) => {
      queryClient.setQueryData(['media', playlistItem.mediaid], playlistItem);
    });

    return playlist;
  };

  const queryKey = ['playlist', playlistId, params];
  const isEnabled = !!playlistId && enabled;

  return useQuery<Playlist | undefined, ApiError>(queryKey, () => callback(playlistId, params), {
    enabled: isEnabled,
    placeholderData: usePlaceholderData && isEnabled ? placeholderData : undefined,
    refetchInterval: (data, _) => {
      if (!data) { return false; }

      const autoRefetch = isTruthyCustomParamValue(data.refetch) || data.playlist.some(isScheduledOrLiveMedia);

      return autoRefetch ? 1000 * 30 : false;
    },
    retry: false,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}
